import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Mail, AlertTriangle, ImagePlus, Users, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminDashboard() {
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        api.get("/admin/summary").then(({ data }) => setSummary(data)).catch(() => {});
    }, []);

    if (!summary) return <div className="text-gray-500" data-testid="admin-dashboard-loading">Loading…</div>;

    const cards = [
        { label: "Total submissions", value: summary.total_submissions, icon: Inbox, to: "/admin/submissions", testid: "card-total" },
        { label: "New submissions", value: summary.new_submissions, icon: Mail, to: "/admin/submissions?status=new", testid: "card-new" },
        { label: "Email queue", value: summary.queued_emails, icon: AlertTriangle, to: "/admin/security", testid: "card-queued" },
        { label: "Users", value: summary.users, icon: Users, to: "/admin/users", testid: "card-users" },
    ];

    return (
        <div data-testid="admin-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="gsf-eyebrow">Overview</div>
                    <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Dashboard</h1>
                </div>
                <ReadinessBadges summary={summary} />
            </div>

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map((c) => (
                    <Link key={c.label} to={c.to} className="gsf-card p-6" data-testid={c.testid}>
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-gray-500">{c.label}</div>
                            <c.icon size={18} className="text-[color:var(--gsf-green-700)]" />
                        </div>
                        <div className="mt-4 font-serif text-4xl font-bold text-[color:var(--gsf-green-700)]">{c.value}</div>
                    </Link>
                ))}
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="gsf-card p-6" data-testid="submissions-by-kind">
                    <h3 className="font-serif text-lg font-semibold text-[color:var(--gsf-green-700)]">Submissions by form</h3>
                    <ul className="mt-4 divide-y divide-gray-100">
                        {Object.entries(summary.counts_by_kind).map(([k, v]) => (
                            <li key={k} className="flex justify-between py-3 text-sm">
                                <span className="capitalize">{k.replace(/_/g, " ")}</span>
                                <span className="font-semibold">{v}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="gsf-card p-6" data-testid="quick-actions">
                    <h3 className="font-serif text-lg font-semibold text-[color:var(--gsf-green-700)]">Quick actions</h3>
                    <div className="mt-4 grid grid-cols-1 gap-3">
                        <Link to="/admin/submissions" className="gsf-btn-outline text-sm justify-center">View submissions</Link>
                        <Link to="/admin/impact-stats" className="gsf-btn-outline text-sm justify-center">Update impact stats</Link>
                        <Link to="/admin/brand-assets" className="gsf-btn-outline text-sm justify-center">Manage brand assets</Link>
                        <Link to="/admin/security" className="gsf-btn-outline text-sm justify-center">Security status</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ReadinessBadges({ summary }) {
    const items = [
        { ok: summary.brand_logo_uploaded, label: "Logo" },
        { ok: summary.brand_icon_uploaded, label: "Icon" },
        { ok: summary.email_provider_configured, label: "Email (Resend)" },
    ];
    return (
        <div className="flex gap-2 flex-wrap" data-testid="readiness-badges">
            {items.map((it) => (
                <span key={it.label}
                    className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 ${it.ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                    {it.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {it.label}
                </span>
            ))}
        </div>
    );
}
